import { useMutation, useQueryClient } from '@tanstack/react-query';
import { conversationsApi } from '../api/conversations';
import type { Conversation, Message, PreferredFormat } from '../types/api';

// Shape returned by `conversationsApi.get(id)` — used by the React
// Query cache for `['conversation', id]`.
type ConversationDetail = { conversation: Conversation; messages: Message[] };

/**
 * Send a user message in the current conversation.
 *
 * **Optimistic UI**: the user's message is added to the conversation
 * cache the moment they hit Enter — they don't have to wait for the
 * backend to round-trip. The composer clears, the bubble appears, and
 * the StatusDisplay below it shows the model is thinking. When the
 * real response arrives, we invalidate and refetch — the temp user
 * message gets replaced by the persisted one (same content, real id),
 * and the assistant's reply slides in below.
 *
 * If the request errors out, we roll the cache back so we don't leave
 * a ghost message in the UI.
 */
export function useSendMessage(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { content: string; preferredFormat: PreferredFormat }) => {
      if (!conversationId) throw new Error('No conversation id');
      return conversationsApi.sendMessage(conversationId, vars.content, vars.preferredFormat);
    },
    onMutate: async (vars) => {
      if (!conversationId) return { prev: undefined };
      const queryKey = ['conversation', conversationId] as const;
      // Cancel any in-flight refetch so it doesn't overwrite our optimistic
      // value before the real response lands.
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<ConversationDetail>(queryKey);
      const tempMessage: Message = {
        id: `temp_${Date.now()}`,
        conversationId,
        role: 'user',
        content: vars.content,
        chartSpec: null,
        preferredFormat: vars.preferredFormat,
        createdAt: new Date().toISOString(),
      };
      if (prev) {
        qc.setQueryData<ConversationDetail>(queryKey, {
          ...prev,
          messages: [...prev.messages, tempMessage],
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back the optimistic update so the failed message doesn't
      // hang in the UI as a ghost.
      if (!conversationId) return;
      const queryKey = ['conversation', conversationId] as const;
      if (ctx?.prev) {
        qc.setQueryData(queryKey, ctx.prev);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useRegenerateMessage(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { msgId: string; content?: string; preferredFormat?: PreferredFormat }) => {
      if (!conversationId) throw new Error('No conversation id');
      return conversationsApi.regenerate(conversationId, vars.msgId, {
        content: vars.content,
        preferredFormat: vars.preferredFormat,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
  });
}
